// 1. URL fragment: BE redirects to /sso-callback#access_token=...&refresh_token=... (primary)
// 2. Document-embedded JSON: window global or <script> tag injected by BE (legacy fallback)
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../contexts/AuthContext';
import {
  buildLoginResponseFromSsoJsonBody,
  isSsoNoInviteError,
  parseSsoCallbackErrorsFromLocation,
  readSsoCallbackJsonFromDocument,
  type SsoDomainStatus,
} from '../services/ssoService';
import { getPostLoginRedirect, clearPostLoginRedirect } from '../utils/auth';
import { requestWebAccess } from '../services/accessRequestService';

const SsoCallback: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithResponse, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<SsoDomainStatus | null>(null);
  const [busy, setBusy] = useState(true);
  const [redirectPath] = useState(() => getPostLoginRedirect());
  const [gmailFlowComplete, setGmailFlowComplete] = useState(false);
  const [isGmailAuthStarted, setIsGmailAuthStarted] = useState(false);
  const [accessRequestState, setAccessRequestState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [accessRequestError, setAccessRequestError] = useState<string | null>(null);
  const [accessRequestSubmittedMessage, setAccessRequestSubmittedMessage] = useState<string | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [accessRequestToken, setAccessRequestToken] = useState('');
  // Guards against React 18 Strict Mode double-invoking this effect in dev (and any
  // other accidental re-run): the effect calls window.history.replaceState to strip
  // the one-time #error_code=.../#access_token=... fragment after reading it, so a
  // second invocation reads an already-stripped URL, finds nothing, and falls through
  // to the "no sign-in data was found" fallback — silently clobbering correctly-parsed
  // state (e.g. domain_status) with a generic error. A ref (not state) survives
  // Strict Mode's mount/cleanup/mount cycle, so this makes the effect run its
  // side-effecting logic exactly once per real navigation to this page.
  const hasProcessedCallbackRef = useRef(false);

  useEffect(() => {
    if (isGmailAuthStarted) {
      return;
    }
    if (hasProcessedCallbackRef.current) {
      return;
    }
    hasProcessedCallbackRef.current = true;
    const handleGmailRedirect = (loginResponse: { access_token?: string; refresh_token?: string }) => {
      const fromGmail = localStorage.getItem('from_gmail');
      if (fromGmail === 'true') {
        setIsGmailAuthStarted(true);
        const redirectUri = localStorage.getItem('gmail_redirect_uri');
        const ssoNonce = localStorage.getItem('gmail_sso_nonce');
        const addonUser = localStorage.getItem('gmail_addon_user');
        if (redirectUri && loginResponse.access_token && loginResponse.refresh_token) {
          localStorage.removeItem('from_gmail');
          localStorage.removeItem('gmail_redirect_uri');
          localStorage.removeItem('gmail_email');
          localStorage.removeItem('gmail_sso_nonce');
          localStorage.removeItem('gmail_addon_user');

          const postPayload: Record<string, unknown> = { sso_auth_response: loginResponse };
          if (addonUser) postPayload.addon_user = addonUser;
          if (ssoNonce) postPayload.sso_nonce = ssoNonce;

          fetch(redirectUri, {
            method: 'POST',
            headers: {
              'Content-Type': 'text/plain',
            },
            body: JSON.stringify(postPayload),
          })
            .then(response => {
              if (!response.ok) {
                console.error('Network response was not ok:', response);
                throw new Error('Network response was not ok.');
              }
              setGmailFlowComplete(true);
              setBusy(false);
              return;
            })
            .catch(error => {
              console.error('Failed to send authentication tokens to the Gmail add-on:', error);
              setError('Failed to send authentication tokens to the Gmail add-on.');
              setErrorCode('GMAIL_TOKEN_SEND_FAILED');
              setBusy(false);
            });
          return true;
        }
      }
      return false;
    };

    setBusy(true);
    setError(null);
    setErrorCode(null);
    setDomainStatus(null);

    const search = window.location.search || '';
    const hash = window.location.hash || '';
    const callbackEmail =
      new URLSearchParams(hash.replace(/^#/, '')).get('email')?.trim().toLowerCase() ||
      new URLSearchParams(search.replace(/^\?/, '')).get('email')?.trim().toLowerCase() ||
      '';
    if (callbackEmail) {
      setRequestEmail(callbackEmail);
    }
    const urlErr = parseSsoCallbackErrorsFromLocation(search, hash);
    if (urlErr?.accessRequestToken) {
      setAccessRequestToken(urlErr.accessRequestToken);
    }

    // Mode 0 (primary): tokens delivered directly in URL fragment (#access_token=...&refresh_token=...)
    const fragParams = new URLSearchParams(hash.replace(/^#/, ''));
    const directAccessToken = fragParams.get('access_token');
    const directRefreshToken = fragParams.get('refresh_token');
    if (directAccessToken && directRefreshToken) {
      const loginResponse = buildLoginResponseFromSsoJsonBody({
        access_token: directAccessToken,
        refresh_token: directRefreshToken,
      });
      if (handleGmailRedirect(loginResponse)) return;
      loginWithResponse(loginResponse);
      window.history.replaceState(null, document.title, window.location.pathname);
      setBusy(false);
      if (redirectPath) {
        clearPostLoginRedirect();
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/supportSpace', { replace: true });
      }
      return;
    }

    // Mode 1: tokens delivered as base64url JSON in a single fragment param
    // (#sso_data=<base64url>) — avoids URI length limits from multiple JWT-sized fields
    const ssoData = fragParams.get('sso_data');
    if (ssoData) {
      try {
        const padded = ssoData + '='.repeat((4 - (ssoData.length % 4)) % 4);
        const body = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
          string,
          unknown
        >;
        const fragAccessToken = typeof body.access_token === 'string' ? body.access_token : null;
        const fragRefreshToken = typeof body.refresh_token === 'string' ? body.refresh_token : null;
        if (fragAccessToken && fragRefreshToken) {
          const loginResponse = buildLoginResponseFromSsoJsonBody(body);
          if (handleGmailRedirect(loginResponse)) {
            return;
          }
          loginWithResponse(loginResponse);
          window.history.replaceState(null, document.title, window.location.pathname);
          setBusy(false);
          if (redirectPath) {
            clearPostLoginRedirect();
            navigate(redirectPath, { replace: true });
          } else {
            navigate('/supportSpace', { replace: true });
          }
          return;
        }
      } catch {
        // malformed sso_data — fall through to document read
      }
    }

    // Mode 2: tokens delivered via document-embedded JSON (legacy fallback)
    const wasAlreadyAuthenticated = isAuthenticated;
    const read = readSsoCallbackJsonFromDocument();

    if (read.status === 'success') {
      const loginResponse = buildLoginResponseFromSsoJsonBody(read.body);
      if (!loginResponse.access_token || !loginResponse.refresh_token) {
        setError('Invalid sign-in response: missing tokens. Please try signing in again.');
        setErrorCode(null);
        setBusy(false);
        window.history.replaceState(null, document.title, window.location.pathname);
        return;
      }

      if (handleGmailRedirect(loginResponse)) {
        return;
      }
      loginWithResponse(loginResponse);
      window.history.replaceState(null, document.title, window.location.pathname);
      setBusy(false);
      if (redirectPath) {
        clearPostLoginRedirect();
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/supportSpace', { replace: true });
      }
      return;
    }

    // A successful token payload is authoritative across all three callback
    // formats. Some SSO providers or redirect layers can leave stale
    // error/message metadata alongside a valid payload; only show the URL
    // error after Mode 0, Mode 1, and Mode 2 success formats are checked.
    if (urlErr) {
      setError(urlErr.message);
      setErrorCode(urlErr.errorCode ?? null);
      setDomainStatus(urlErr.domainStatus ?? null);
      setBusy(false);
      window.history.replaceState(null, document.title, window.location.pathname);
      return;
    }

    if (read.status === 'missing') {
      if (wasAlreadyAuthenticated) {
        setBusy(false);
        window.history.replaceState(null, document.title, window.location.pathname);
        if (redirectPath) {
          clearPostLoginRedirect();
          navigate(redirectPath, { replace: true });
        } else {
          navigate('/supportSpace', { replace: true });
        }
        return;
      }
      setError(
        'No sign-in data was found on this page. After OAuth, the server should include the token JSON in the HTML (for example a script tag or window global).'
      );
      setErrorCode(null);
      setBusy(false);
      window.history.replaceState(null, document.title, window.location.pathname);
      return;
    }

    if (read.status === 'error') {
      setError(read.message);
      setErrorCode(read.errorCode ?? null);
      setBusy(false);
      window.history.replaceState(null, document.title, window.location.pathname);
      return;
    }
  }, [navigate, loginWithResponse, isAuthenticated, redirectPath, isGmailAuthStarted]);

  useEffect(() => {
    if (gmailFlowComplete) {
      const timer = setTimeout(() => {
        window.close();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [gmailFlowComplete]);

  const openRequestDialog = () => {
    setAccessRequestError(null);
    setRequestDialogOpen(true);
  };

  const handleRequestAccess = async () => {
    setAccessRequestState('submitting');
    setAccessRequestError(null);
    try {
      const message = await requestWebAccess(requestEmail, accessRequestToken);
      setAccessRequestSubmittedMessage(message);
      setRequestDialogOpen(false);
      setAccessRequestState('submitted');
    } catch (requestError) {
      setAccessRequestState('error');
      setAccessRequestError(
        requestError instanceof Error ? requestError.message : 'Unable to submit your access request. Please try again.',
      );
    }
  };

  if (gmailFlowComplete) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          maxWidth: 480,
          mx: 'auto',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Authentication Successful
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          You can now return to Gmail. This window will close automatically.
        </Typography>
      </Box>
    );
  }

  if (busy) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const isNoInviteError = isSsoNoInviteError(errorCode, error);
    // Non-no-invite SSO errors (bad state, provider failure, etc.) keep the
    // original "Go to Register" CTA — unaffected by domain_status.
    // No-invite errors branch by domain_status instead of offering /register,
    // which is itself invite-gated and would just loop back here (see PR #452):
    //   known   -> real company already on Prism7, only an admin invite can help.
    //   unknown/skipped/missing -> no company found for this domain (or a public
    //     email domain) -> offer to create one via /companySignup.
    const shouldShowCompanySignup = isNoInviteError && domainStatus !== 'known';
    const shouldShowAskAdminOnly = isNoInviteError && domainStatus === 'known';
    // Older SSO responses may omit domain_status while still returning the
    // admin-invite guidance. Match that guidance explicitly so unrelated SSO
    // failures continue to show the Register CTA.
    const shouldOfferAccessRequest =
      !shouldShowCompanySignup &&
      (shouldShowAskAdminOnly ||
        errorCode === 'access_denied' ||
        /ask your admin|invite you to the application|not currently a member|access denied|member of this application/i.test(error));
    const shouldShowRegister = !isNoInviteError && !shouldOfferAccessRequest;

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#eef1f5',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 500,
            bgcolor: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Brand header — logo only, no background/border */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2.5,
            }}
          >
            <Box
              component="img"
              src="/fulllogo.png"
              alt="Prism7"
              sx={{ height: 36, objectFit: 'contain' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </Box>

          {/* Body */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 3,
              pt: 4,
              pb: 4,
              width: '100%',
            }}
          >
            {/* Shield icon ring */}
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                bgcolor: '#fef2f2',
                border: '1.5px solid #fecaca',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <GppBadOutlinedIcon sx={{ fontSize: 40, color: '#dc2626' }} />
            </Box>

            {shouldOfferAccessRequest && accessRequestState === 'submitted' ? (
              <Alert severity="success" sx={{ width: 'calc(100% - 64px)', mb: 1.5 }}>
                {accessRequestSubmittedMessage || 'Your access request has been sent to your company admin.'}
              </Alert>
            ) : null}

            {shouldOfferAccessRequest && accessRequestState === 'error' ? (
              <Alert severity="error" sx={{ width: 'calc(100% - 64px)', mb: 1.5 }}>
                {accessRequestError}
              </Alert>
            ) : null}

            <Chip
              label="SSO ACCESS DENIED"
              size="small"
              sx={{
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                fontWeight: 600,
                fontSize: '0.68rem',
                letterSpacing: 0.6,
                border: '0.5px solid #fecaca',
                mb: 1.75,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
              textAlign="center"
              color="#111827"
              sx={{ mb: 2 }}
            >
              Access not granted
            </Typography>

            {/* Admin info box */}
            <Box
              sx={{
                width: 'calc(100% - 64px)',
                bgcolor: '#f9fafb',
                border: '0.5px solid #e5e7eb',
                borderRadius: '10px',
                px: 2,
                py: 1.75,
                mb: 1.5,
              }}
            >
              <Typography variant="body2" color="#6b7280" textAlign="center" sx={{ mb: 0.5 }}>
                You're not currently a member of this application.
              </Typography>
              <Typography variant="body2" color="#6b7280" textAlign="center" fontWeight={500}>
                {shouldShowAskAdminOnly
                  ? 'This domain is already on Prism7 — ask your admin to invite you.'
                  : shouldShowCompanySignup
                    ? 'No company found for this domain — set up your company to get started.'
                    : 'Ask your admin to invite you to the application.'}
              </Typography>
            </Box>

            {/* New company hint */}
            {shouldShowRegister || shouldShowCompanySignup ? (
              <Box
                sx={{
                  width: 'calc(100% - 62px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: '#f9fafb',
                  border: '0.5px solid #e5e7eb',
                  borderRadius: '10px',
                  px: 1.75,
                  py: 1.5,
                  mb: 2.5,
                }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 15, color: '#9ca3af', flexShrink: 0 }}
                />
                <Typography variant="body2" color="#6b7280" textAlign="center" lineHeight={1.6}>
                  {shouldShowCompanySignup ? (
                    <>
                      If you're the first person from your company here, click{' '}
                      <Box component="span" sx={{ color: '#1cabde', fontWeight: 600 }}>
                        Set Up Your Company
                      </Box>{' '}
                      to create a new account.
                    </>
                  ) : (
                    <>
                      If you need to login as a new company, click{' '}
                      <Box component="span" sx={{ color: '#1cabde', fontWeight: 600 }}>
                        Register
                      </Box>{' '}
                      to create a new account.
                    </>
                  )}
                </Typography>
              </Box>
            ) : null}

            {/* Primary CTA */}
            {shouldShowCompanySignup ? (
              <Button
                component={RouterLink}
                to="/companySignup"
                variant="contained"
                size="large"
                sx={{
                  width: 'calc(100% - 24px)',
                  mb: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  py: 1.3,
                  bgcolor: '#1cabde',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1899c7', boxShadow: 'none' },
                }}
              >
                Set Up Your Company
              </Button>
            ) : shouldShowRegister ? (
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  width: 'calc(100% - 24px)',
                  mb: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  py: 1.3,
                  bgcolor: '#1cabde',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1cabde', boxShadow: 'none' },
                }}
              >
                Register
              </Button>
            ) : null}

            {/* Secondary CTA */}
            {shouldOfferAccessRequest ? (
              <Button
                variant="contained"
                size="large"
                onClick={openRequestDialog}
                disabled={accessRequestState === 'submitting' || accessRequestState === 'submitted'}
                startIcon={accessRequestState === 'submitting' ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{
                  width: 'calc(100% - 24px)',
                  mb: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  py: 1.3,
                  bgcolor: '#1cabde',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1899c7', boxShadow: 'none' },
                }}
              >
                {accessRequestState === 'submitted' ? 'Access Request Sent' : 'Request Access from Admin'}
              </Button>
            ) : null}

            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size="large"
              sx={{
                width: 'calc(100% - 24px)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                py: 1.3,
                borderColor: '#1cabde',
                borderWidth: '1.5px',
                color: '#1cabde',
                '&:hover': {
                  borderColor: '#1899c7',
                  borderWidth: '1.5px',
                  color: '#1899c7',
                  bgcolor: '#f0f9ff',
                },
              }}
            >
              Back to Login
            </Button>

            <Dialog
              open={requestDialogOpen}
              onClose={() => accessRequestState !== 'submitting' && setRequestDialogOpen(false)}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>Request application access</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This request will use the email from your SSO login. Your admin will receive an access request.
                </Typography>
                {/* Read-only display, not an editable field — the email always comes from the
                    SSO login, never something the admin/user types here. */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Work email
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {requestEmail}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setRequestDialogOpen(false)} disabled={accessRequestState === 'submitting'}>
                  Cancel
                </Button>
                <Button
                  autoFocus
                  variant="contained"
                  onClick={() => void handleRequestAccess()}
                  disabled={!requestEmail.trim() || accessRequestState === 'submitting'}
                >
                  {accessRequestState === 'submitting' ? 'Submitting…' : 'Submit request'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default SsoCallback;
